/**
 * « Révision d'été » — reprendre contact avant la rentrée.
 *
 * Demandé par Hedi le 26 août 2026, à six jours de la rentrée, et pour une
 * raison qui vaut d'être écrite : **rien n'a jamais été servi à un enfant.** Le
 * pool porte des milliers d'exercices, la production tourne, et personne ne s'est
 * assis devant. Cet écran est le chemin le plus court entre un enfant et un
 * exercice — pas de séance, pas de tuteur, pas de flux : un énoncé, une réponse,
 * une correction.
 *
 * **Ce qu'il annonce est ce qu'il sert.** Les référentiels du niveau précédent
 * sont vides, donc ce sont les premières notions de l'année qui vient qui
 * arrivent, dans la bande basse. Le serveur le dit (`anticipation`) et l'écran
 * le répète à l'enfant. Une interface qui appellerait ça « révision » sans
 * préciser mentirait sur la seule chose que l'enfant peut vérifier lui-même.
 *
 * **Aucun score, ici comme ailleurs** (invariant 1). Le nombre affiché est une
 * quantité d'exercices restants, jamais une réussite ni un pourcentage : une
 * révision libre où l'on ignore combien il reste est une corvée sans horizon,
 * mais un compte de bonnes réponses serait la note sur vingt qui revient par la
 * fenêtre.
 *
 * L'exercice lui-même et sa correction vivent dans `ExerciceEtCorrige`, partagés
 * avec l'écran du jour depuis le 3 septembre 2026.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { call } from "@/api/client";
import type { CompetenceARevoir, ExerciseOut, PlanDeRevision } from "@/api/schema";
import { ExerciceEtCorrige } from "@/child/ExerciceEtCorrige";
import { etiquette } from "@/ui/matieres";
import { Button, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";

type Etat =
  | { quoi: "chargement" }
  | { quoi: "panne"; message: string }
  // **Épuisé n'est pas une panne**, et l'enfant ne doit pas lire la même chose
  // dans les deux cas. Un sujet sans exercice disponible est un état normal ;
  // l'afficher en rouge apprend à Baya qu'elle a cassé quelque chose en
  // cliquant. Trouvé par Hedi en testant avec elle.
  | { quoi: "epuise"; sujet: string }
  | { quoi: "choix"; plan: PlanDeRevision }
  | { quoi: "exercice"; plan: PlanDeRevision; sur: CompetenceARevoir; item: ExerciseOut };

export function RevisionScreen() {
  const navigate = useNavigate();
  const [etat, setEtat] = useState<Etat>({ quoi: "chargement" });

  const charger = useCallback(async () => {
    setEtat({ quoi: "chargement" });
    try {
      const plan = await call("planDeRevision");
      setEtat({ quoi: "choix", plan });
    } catch {
      setEtat({ quoi: "panne", message: "Je n'arrive pas à préparer ta révision." });
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const commencer = async (plan: PlanDeRevision, sur: CompetenceARevoir) => {
    // **Une compétence en série s'ouvre ailleurs**, avec son chronomètre
    // (`exercise` §10) : depuis le 10 septembre 2026 un flash ne se sert plus à
    // l'unité, et `exerciceDeRevision` rend 404 sur une compétence qui n'a que
    // ça. Le drapeau vient du plan et n'est pas recalculé ici — même raison que
    // dans « Mon travail », qui porte la même branche.
    if (sur.serie) {
      navigate(`/eleve/flash/${encodeURIComponent(sur.competency_id)}`, {
        state: { retour: "/eleve/revision" },
      });
      return;
    }
    setEtat({ quoi: "chargement" });
    try {
      // **`exerciceDeRevision` et non `nextExercise`.** Celui-ci tire à la
      // difficulté de la maîtrise mesurée — 1 500 tant que rien n'est mesuré —
      // et ne cherche que les `free_input` : les deux réglages d'une séance,
      // faux pour une reprise de contact, et l'écran répondait « plus rien à
      // faire sur ce sujet » sur des compétences qu'il venait d'annoncer.
      const item = await call("exerciceDeRevision", {
        body: { competency_id: sur.competency_id },
      });
      setEtat({ quoi: "exercice", plan, sur, item });
    } catch {
      // Le pool peut s'être vidé entre le plan et le tirage — le délai de repos
      // d'un exercice court pendant qu'on lit l'écran. Ce n'est pas une panne,
      // c'est un « pas maintenant », et l'enfant n'a pas à lire un message
      // d'erreur pour ça.
      setEtat({ quoi: "epuise", sujet: sur.libelle });
    }
  };

  if (etat.quoi === "chargement") return <Loading label="Je prépare…" />;

  if (etat.quoi === "panne") {
    return (
      <Screen title="Révision d'été">
        <ErrorNote>{etat.message}</ErrorNote>
        <Button onClick={() => void charger()}>Revenir à la liste</Button>
      </Screen>
    );
  }

  if (etat.quoi === "epuise") {
    return (
      <Screen title="Révision d'été">
        <Empty>Tu as fait le tour de ce sujet. Choisis-en un autre.</Empty>
        <Button onClick={() => void charger()}>← Revenir à la liste</Button>
      </Screen>
    );
  }

  if (etat.quoi === "choix") {
    return (
      <Screen title="Révision d'été">
        <p className="lede">
          {etat.plan.anticipation
            ? "Ce ne sont pas des révisions de l'an dernier : ce sont les premières notions de l'année qui commence. De quoi arriver en terrain connu le jour de la rentrée."
            : "De quoi reprendre contact avec ce que tu as vu l'an dernier."}
        </p>
        {etat.plan.competences.length === 0 ? (
          <Empty>Rien à réviser pour le moment.</Empty>
        ) : (
          <ul className="revision__liste">
            {etat.plan.competences.map((c) => (
              <li key={c.competency_id}>
                <button
                  type="button"
                  className="revision__item"
                  onClick={() => void commencer(etat.plan, c)}
                >
                  <span className="revision__matiere">{etiquette(c.matiere)}</span>
                  <span className="revision__libelle">{c.libelle}</span>
                  <span className="revision__reste">
                    {c.disponibles} exercice{c.disponibles > 1 ? "s" : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Screen>
    );
  }

  return (
    <Screen title={etiquette(etat.sur.matiere)}>
      <ExerciceEtCorrige
        item={etat.item}
        matiere={etat.sur.matiere}
        retour={() => void charger()}
        suivant={() => void commencer(etat.plan, etat.sur)}
      />
    </Screen>
  );
}
